import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { RefreshCw, Loader2, TrendingDown, Minus, TrendingUp, Info } from 'lucide-react';
import SectionPdfExport, { buildColumns } from '@/components/SectionPdfExport';

const fmt = (v) => {
  if (v === undefined || v === null || isNaN(v)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
};
const pct = (v) => `${(v * 100).toFixed(1)}%`;

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-zinc-900/95 backdrop-blur-sm border border-zinc-800 p-3 rounded-lg shadow-xl">
      <p className="text-xs text-zinc-400 mb-1 font-mono">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-zinc-400">{p.name}:</span>
          <span className="font-mono text-zinc-200">{fmt(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

// Business Profile Presets
const businessProfiles = [
  {
    name: 'Declining Business',
    icon: TrendingDown,
    color: 'text-red-400',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/30',
    growth: [-0.10, -0.05, 0, 0, 0],
    description: 'Stabilization and recovery to flat',
  },
  {
    name: 'Stable Business',
    icon: Minus,
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/30',
    growth: [0, 0.02, 0.02, 0.03, 0.03],
    description: 'Modest organic growth',
  },
  {
    name: 'Growing Business',
    icon: TrendingUp,
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/30',
    growth: [0.05, 0.10, 0.10, 0.05, 0.05],
    description: 'Expansion phase',
  },
];

const defaultScenarios = [
  { name: 'Flat (Status Quo)', key: 'A', growth: [0, 0, 0, 0, 0], color: '#6366f1' },
  { name: 'Recovery (Re-Growth)', key: 'B', growth: [0.10, 0.10, 0.05, 0.05, 0.05], color: '#10b981' },
  { name: 'Continued Decline', key: 'C', growth: [-0.10, -0.05, 0, 0, 0], color: '#ef4444' },
];

export default function ScenarioAnalysisPage({ data, results, analysis, saveAnalysis, loading }) {
  const scenarios = results?.scenarios || {};
  const storedScenarios = data?.scenarios || [];

  const [scenarioInputs, setScenarioInputs] = useState(() => {
    return defaultScenarios.map((s, i) => ({
      ...s,
      growth: storedScenarios[i]?.growth_rates || s.growth,
      exit_multiple: storedScenarios[i]?.exit_multiple || 3.0,
    }));
  });

  const handleRecalculate = useCallback(async () => {
    if (!analysis?.id) return;
    const updatedScenarios = scenarioInputs.map(s => ({
      name: s.name,
      label: s.key,
      growth_rates: s.growth,
      exit_multiple: s.exit_multiple,
    }));
    await saveAnalysis({
      ...data,
      name: analysis.name || 'Analysis',
      scenarios: updatedScenarios,
    });
  }, [scenarioInputs, data, analysis, saveAnalysis]);

  const updateGrowth = (sIdx, yIdx, val) => {
    setScenarioInputs(prev => {
      const next = [...prev];
      next[sIdx] = { ...next[sIdx], growth: [...next[sIdx].growth] };
      next[sIdx].growth[yIdx] = val;
      return next;
    });
  };

  const applyPreset = (sIdx, preset) => {
    setScenarioInputs(prev => {
      const next = [...prev];
      next[sIdx] = { ...next[sIdx], growth: [...preset.growth] };
      return next;
    });
  };

  const askingPrice = data?.seller_deal?.purchase_price || 449000;

  // Comparison chart data
  const comparisonData = Object.entries(scenarios).map(([name, s]) => ({
    name: name.length > 20 ? name.substring(0, 18) + '...' : name,
    'Enterprise Value': s.enterprise_value,
    'Asking Price': askingPrice,
  }));

  if (!data?.dcf_assumptions?.base_revenue) {
    return (
      <div className="flex items-center justify-center min-h-[40vh] text-zinc-500 text-sm" data-testid="scenarios-page">
        Import data to view scenario analysis
      </div>
    );
  }

  // Prepare data for PDF export
  const scenarioPdfData = Object.entries(scenarios).map(([name, s]) => ({
    name,
    enterprise_value: s.enterprise_value,
    vs_asking: s.enterprise_value - askingPrice,
    implied_multiple: s.implied_multiple,
    assessment: s.enterprise_value > askingPrice ? 'Supports' : 'Below'
  }));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="scenarios-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-zinc-100 font-['Manrope']">Scenario Analysis</h2>
          <p className="text-sm text-zinc-400 mt-1">Three scenarios modeling range of outcomes for new owner</p>
        </div>
        <div className="flex items-center gap-2">
          <SectionPdfExport
            title="Scenario Analysis"
            analysisName={analysis?.name}
            rows={scenarioPdfData}
            columns={buildColumns([
              { label: 'Scenario', key: 'name' },
              { label: 'Enterprise Value', key: 'enterprise_value', format: 'currency' },
              { label: 'vs Asking', key: 'vs_asking', format: 'currency' },
              { label: 'Implied Multiple', key: 'implied_multiple', format: 'number' },
              { label: 'Assessment', key: 'assessment' },
            ])}
          />
          <Button
            onClick={handleRecalculate}
            disabled={loading}
            className="bg-indigo-600 hover:bg-indigo-500 text-white"
            data-testid="recalculate-scenarios-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Recalculate Scenarios
          </Button>
        </div>
      </div>

      {/* Business Profile Presets Guide */}
      <Card className="bg-zinc-900/60 border-zinc-800/60">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Info className="w-4 h-4 text-indigo-400" />
            <CardTitle className="text-sm font-medium text-zinc-300 font-['Manrope']">Business Profile Presets</CardTitle>
          </div>
          <p className="text-xs text-zinc-500 mt-1">Click a preset to apply growth rates to a scenario. Values remain editable.</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {businessProfiles.map((profile) => {
              const Icon = profile.icon;
              return (
                <div
                  key={profile.name}
                  className={`p-3 rounded-lg border ${profile.borderColor} ${profile.bgColor}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className={`w-4 h-4 ${profile.color}`} />
                    <span className={`text-sm font-medium ${profile.color}`}>{profile.name}</span>
                  </div>
                  <p className="text-[10px] text-zinc-500 mb-2">{profile.description}</p>
                  <div className="flex gap-1 mb-2">
                    {profile.growth.map((g, i) => (
                      <span
                        key={i}
                        className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${g > 0 ? 'bg-emerald-500/20 text-emerald-400' : g < 0 ? 'bg-red-500/20 text-red-400' : 'bg-zinc-700/50 text-zinc-400'}`}
                      >
                        Yr{i + 1}: {g >= 0 ? '+' : ''}{(g * 100).toFixed(0)}%
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-1">
                    {['A', 'B', 'C'].map((key, sIdx) => (
                      <button
                        key={key}
                        onClick={() => applyPreset(sIdx, profile)}
                        className="text-[9px] px-2 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-300 transition-colors"
                        data-testid={`apply-${profile.name.toLowerCase().replace(' ', '-')}-to-${key}`}
                      >
                        Apply to {key}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Scenario Inputs with Detailed Projections */}
      {scenarioInputs.map((s, sIdx) => {
        const scenarioResult = scenarios[s.name] || {};
        const projections = scenarioResult.projections || [];
        
        return (
          <Card key={s.key} className="bg-zinc-900/60 border-zinc-800/60">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                  <CardTitle className="text-sm font-medium text-zinc-300 font-['Manrope']">
                    Scenario {s.key}: {s.name}
                  </CardTitle>
                </div>
                <div className="flex items-center gap-4 text-xs">
                  <div>
                    <span className="text-zinc-500">Terminal Value: </span>
                    <span className="font-mono text-amber-400">{fmt(scenarioResult.terminal_value)}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Enterprise Value: </span>
                    <span className="font-mono text-indigo-400 font-semibold">{fmt(scenarioResult.enterprise_value)}</span>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Growth Rate Sliders */}
              <div>
                <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Growth Rates (Manually Editable)</span>
                <div className="grid grid-cols-5 gap-4 mt-2">
                  {s.growth.map((g, yIdx) => (
                    <div key={yIdx}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] text-zinc-500">Year {yIdx + 1}</span>
                        <span className={`font-mono text-xs font-medium ${g >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{pct(g)}</span>
                      </div>
                      <Slider
                        value={[g]}
                        min={-0.30}
                        max={0.30}
                        step={0.01}
                        onValueChange={([v]) => updateGrowth(sIdx, yIdx, v)}
                        data-testid={`scenario-${s.key}-year-${yIdx + 1}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Detailed Projections Table */}
              {projections.length > 0 && (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-zinc-800/60 hover:bg-transparent">
                        <TableHead className="text-zinc-400 text-[10px]">Year</TableHead>
                        <TableHead className="text-zinc-400 text-[10px] text-right">Growth</TableHead>
                        <TableHead className="text-zinc-400 text-[10px] text-right">Revenue</TableHead>
                        <TableHead className="text-zinc-400 text-[10px] text-right">CapEx</TableHead>
                        <TableHead className="text-zinc-400 text-[10px] text-right">NWC Change</TableHead>
                        <TableHead className="text-zinc-400 text-[10px] text-right">FCF</TableHead>
                        <TableHead className="text-zinc-400 text-[10px] text-right">PV of FCF</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {projections.map((p) => (
                        <TableRow key={p.year} className="border-zinc-800/40 hover:bg-zinc-800/20">
                          <TableCell className="font-mono text-[10px] text-zinc-200">Year {p.year}</TableCell>
                          <TableCell className={`text-right font-mono text-[10px] ${p.growth_rate >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {pct(p.growth_rate)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-[10px] text-zinc-200">{fmt(p.revenue)}</TableCell>
                          <TableCell className="text-right font-mono text-[10px] text-red-400">{fmt(p.capex ? -p.capex : 0)}</TableCell>
                          <TableCell className={`text-right font-mono text-[10px] ${p.nwc_change < 0 ? 'text-amber-400' : 'text-zinc-400'}`}>
                            {fmt(p.nwc_change || 0)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-[10px] text-emerald-400">{fmt(p.fcf)}</TableCell>
                          <TableCell className="text-right font-mono text-[10px] text-indigo-400">{fmt(p.pv_fcf)}</TableCell>
                        </TableRow>
                      ))}
                      {/* Summary row */}
                      <TableRow className="border-zinc-800/60 bg-zinc-800/30">
                        <TableCell colSpan={5} className="text-right text-[10px] text-zinc-400">Sum PV of FCF (Years 1-5)</TableCell>
                        <TableCell className="text-right font-mono text-[10px] text-indigo-400 font-semibold">{fmt(scenarioResult.sum_pv_fcf)}</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                      <TableRow className="border-zinc-800/60 bg-zinc-800/30">
                        <TableCell colSpan={5} className="text-right text-[10px] text-zinc-400">+ PV of Terminal Value (Year 5 FCF × {data?.dcf_assumptions?.exit_multiple || 3.0}x)</TableCell>
                        <TableCell className="text-right font-mono text-[10px] text-amber-400 font-semibold">{fmt(scenarioResult.pv_terminal)}</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                      <TableRow className="border-zinc-800/60 bg-indigo-500/10">
                        <TableCell colSpan={5} className="text-right text-[10px] text-indigo-300 font-medium">= Enterprise Value</TableCell>
                        <TableCell className="text-right font-mono text-sm text-indigo-400 font-bold">{fmt(scenarioResult.enterprise_value)}</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* Comparison Chart */}
      {comparisonData.length > 0 && (
        <Card className="bg-zinc-900/60 border-zinc-800/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-zinc-300">Scenario Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={comparisonData} barGap={8}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis dataKey="name" tick={{ fill: '#71717a', fontSize: 10 }} />
                <YAxis tick={{ fill: '#71717a', fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000000).toFixed(1)}M`} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 11, color: '#a1a1aa' }} />
                <Bar dataKey="Enterprise Value" fill="#6366f1" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Asking Price" fill="#f59e0b" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Comparison Summary Table */}
      <Card className="bg-zinc-900/60 border-zinc-800/60 overflow-hidden">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-zinc-300">Scenario Comparison Summary</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800/60 hover:bg-transparent">
                <TableHead className="text-zinc-400 text-xs">Scenario</TableHead>
                <TableHead className="text-zinc-400 text-xs text-right">Enterprise Value</TableHead>
                <TableHead className="text-zinc-400 text-xs text-right">vs Asking ({fmt(askingPrice)})</TableHead>
                <TableHead className="text-zinc-400 text-xs text-right">Implied Multiple</TableHead>
                <TableHead className="text-zinc-400 text-xs text-right">Assessment</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(scenarios).map(([name, s]) => {
                const diff = s.enterprise_value - askingPrice;
                const supports = diff > 0;
                return (
                  <TableRow key={name} className="border-zinc-800/40 hover:bg-zinc-800/20">
                    <TableCell className="text-xs text-zinc-300 font-medium">{name}</TableCell>
                    <TableCell className="text-right font-mono text-sm text-zinc-200">{fmt(s.enterprise_value)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      <span className={supports ? 'text-emerald-400' : 'text-red-400'}>
                        {diff >= 0 ? '+' : ''}{fmt(diff)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs text-zinc-300">{s.implied_multiple?.toFixed(2)}x</TableCell>
                    <TableCell className="text-right">
                      <Badge variant="outline" className={`text-[10px] ${supports ? 'border-emerald-500/30 text-emerald-400' : 'border-red-500/30 text-red-400'}`}>
                        {supports ? 'Supports deal' : 'Below asking'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* NWC Change Explanation */}
      <Card className="bg-zinc-900/60 border-amber-500/20">
        <CardContent className="p-4 text-xs text-zinc-400 space-y-2">
          <p className="text-amber-400 font-medium">Understanding NWC Change:</p>
          <ul className="list-disc list-inside space-y-1 text-zinc-500">
            <li><span className="text-amber-400">Negative NWC Change</span> = Cash outflow needed to fund growth (more receivables, inventory)</li>
            <li><span className="text-zinc-400">$0 NWC Change</span> = Flat or declining revenue requires no additional working capital investment</li>
            <li>NWC is calculated as: <span className="font-mono text-zinc-300">-MAX(Revenue Increase, 0) × Working Capital %</span></li>
          </ul>
        </CardContent>
      </Card>

      {/* Context note */}
      <Card className="bg-zinc-900/60 border-indigo-500/20">
        <CardContent className="p-4 text-xs text-zinc-400 space-y-1">
          <p className="text-indigo-400 font-medium">Retiring Owner Context:</p>
          <p>The seller is a baby boomer approaching retirement. Revenue decline ($3.9M to $1.7M) and officer salary reduction ($464K to $165K) are consistent with owner disengagement, not structural market failure. The three scenarios model the range of outcomes depending on the new owner's ability to re-engage the business.</p>
        </CardContent>
      </Card>
    </div>
  );
}
